import { FC, lazy, Suspense } from "react";
import { CheckIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";
import BookingOverview from "./BookingOverview";
import FollowScroll from "./FollowScroll";
import DesktopExtras from "./DesktopExtras";
import DesktopProtection from "./DesktopProtection";
import DetailsStep from "./DetailsStep";
import { dollars } from "./dollars";
import PolicyConsent from "./PolicyConsent";
import ReviewStep from "./ReviewStep";
import type { PhoneBookingProps } from "./PhoneBooking";
import { PAY, REVIEW, STEP_DEFS } from "./steps";
import { useStepsController } from "./useStepsController";

// Loaded when the payment step opens, as on phones: the checkout pulls in the whole payment stack.
const CheckOutPagePageMain = lazy(() => import("designs/Common/CheckOutPage/CheckOutPage"));

const Dot: FC<{ state: "done" | "current" | "todo"; n?: number }> = ({ state, n }) => (
  <span
    aria-hidden="true"
    className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border-[1.5px] text-xs font-extrabold ${
      state === "done"
        ? "border-text-color bg-text-color text-bg-color"
        : state === "current"
          ? "border-primary-6000 bg-primary-6000 text-on-primary"
          : "border-secondary-100 bg-card-color text-text-color/60"
    }`}
  >
    {state === "done" ? <CheckIcon className="h-3.5 w-3.5 stroke-[3]" /> : n}
  </span>
);

/**
 * The booking in steps on desktop (1NOW-11173), laid out like Sixt: a stepper across the top, the step in
 * full detail on the left, a sticky "Booking overview" with the total and the main button on the right.
 *
 * It is the phone booking's second renderer: the props are the same PhoneBookingProps and everything the
 * steps do is useStepsController's, so nothing about the booking is decided here. Which step shows is the
 * address's (`?step=`, useBookingFlow), so Back and Forward move between the steps.
 */
const DesktopBooking: FC<PhoneBookingProps> = (p) => {
  const c = useStepsController(p);
  if (!p.open) return null;

  const payNote =
    p.step === PAY && c.later
      ? "Accept the agreement and policies, then reserve the car and pay later."
      : p.step === PAY && c.total !== null
        ? `Accept the agreement and policies. Your card is charged ${dollars(c.pay.depositNow || c.total)} when you press Pay and book.`
        : STEP_DEFS[p.step - 1].note;
  const prev = p.step > 1 ? STEP_DEFS[c.prevStep - 1].label : "vehicle";

  return (
    <div data-testid="desktop-booking" className="DesktopBooking text-text-color">
      <nav aria-label="Booking steps" className="border-b border-secondary-100 bg-card-color">
        <ol className="container flex items-center py-4 text-sm">
          <li className="flex items-center gap-2 font-bold">
            <button type="button" onClick={() => p.setOpen(false)} aria-label="Go to Vehicle" className="flex items-center gap-2">
              <Dot state="done" />
              Vehicle
            </button>
          </li>
          {c.shownSteps.map(({ key, label, step: n }, i) => {
            const state = n < p.step ? "done" : n === p.step ? "current" : "todo";
            return (
              <li key={key} aria-current={state === "current" ? "step" : undefined} className="flex flex-1 items-center">
                <span
                  aria-hidden="true"
                  className={`mx-3 h-0.5 min-w-[12px] flex-1 ${n <= p.step ? "bg-text-color" : "bg-secondary-100"}`}
                />
                {state === "done" ? (
                  <button
                    type="button"
                    onClick={() => p.setStep(n)}
                    aria-label={`Go to ${label}`}
                    className="flex items-center gap-2 whitespace-nowrap font-bold"
                  >
                    <Dot state="done" />
                    {label}
                  </button>
                ) : (
                  <span className={`flex items-center gap-2 whitespace-nowrap ${state === "current" ? "font-extrabold" : "text-text-color/60 font-bold"}`}>
                    <Dot state={state} n={i + 1} />
                    {label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="container pb-16 pt-8">
        <div className="desktop-booking-grid grid gap-10 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_380px]">
          <section aria-labelledby="desktop-step-title" className="min-w-0">
            <h1 id="desktop-step-title" className="text-2xl font-semibold leading-tight xl:text-[28px]">
              {STEP_DEFS[p.step - 1].title}
            </h1>
            <p className="mt-1 text-sm opacity-70">{payNote}</p>

            <div className="mt-6">
              {p.step === 1 && (
                <div className="rounded-3xl border border-secondary-100 bg-card-color p-6">
                  <DetailsStep fields={p.details.fields} confirm={p.details.confirm} wide />
                </div>
              )}
              {p.step === 2 && (
                <DesktopProtection
                  products={p.protection.products}
                  loading={p.protection.loading}
                  notice={p.protection.notice}
                  selectedValue={p.protection.selectedValue}
                  onSelect={p.protection.onSelect}
                  bgColor={p.companyDetails?.bg_color}
                  proof={p.protection.proof}
                />
              )}
              {p.step === 3 && <DesktopExtras {...p.extras} bgColor={p.companyDetails?.bg_color} />}
              {p.step === REVIEW && (
                <ReviewStep
                  wide
                  car={{ name: p.car?.name, photo: p.photo }}
                  pickup={p.trip.pickup}
                  dropoff={p.trip.dropoff}
                  protection={c.protectionLine}
                  extras={c.extraLines}
                  driver={{
                    firstName: p.details.fields.firstName.value,
                    lastName: p.details.fields.lastName.value,
                    email: p.details.fields.email.value,
                    phone: p.details.fields.phone.value,
                  }}
                  onEditTrip={p.onEditTrip}
                  onEditDriver={() => p.onEditStep(1)}
                  onEditProtection={() => p.onEditStep(2)}
                  onEditExtras={c.hideExtras ? undefined : () => p.onEditStep(3)}
                />
              )}
              {p.step === PAY && (
                <div className="rounded-3xl border border-secondary-100 bg-card-color p-6">
                  {c.later ? (
                    <div className="text-sm">
                      <b className="line-clamp-2 break-words text-base" title={p.car?.name}>{p.car?.name}</b>
                      <span className="block opacity-70">{p.trip.range}</span>
                      <p className="mt-3">Reserve it now and pay later.</p>
                    </div>
                  ) : (
                    <Suspense fallback={<p className="py-10 text-center text-sm opacity-70">Loading payment…</p>}>
                      <CheckOutPagePageMain
                        embedded
                        accepted={p.accepted}
                        payRef={c.payRef}
                        onPayState={c.setPay}
                        draft={p.payDraft}
                        onDraft={p.setPayDraft}
                      />
                    </Suspense>
                  )}
                  <div className="mt-6 border-t border-secondary-100 pt-5">
                    <PolicyConsent
                      accepted={p.accepted}
                      onChange={p.setAccepted}
                      companyName={p.companyName}
                      active={p.open && p.step === PAY}
                      agreementUrl={c.agreementUrl}
                      drawer
                    />
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => (p.step > 1 ? p.setStep(c.prevStep) : p.setOpen(false))}
              className="mt-6 inline-flex items-center gap-1 text-sm font-bold underline underline-offset-2"
            >
              <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
              Back to {prev.toLowerCase()}
            </button>
          </section>

          <div>
            <FollowScroll bounds=".desktop-booking-grid">
            <BookingOverview
              car={{ name: p.car?.name, photo: p.photo }}
              // The review shows the trip (with its Edit) in the main column: the card does not repeat it.
              trip={p.step === REVIEW ? undefined : p.trip}
              onEditTrip={p.onEditTrip}
              invoice={c.bill}
              quoted
              carName={p.car?.name}
              label={c.barLabel}
              onClick={c.onBar}
              busy={c.busy}
              disabled={c.disabled}
              secondary={c.payLaterButton}
            />
            </FollowScroll>
          </div>
        </div>
      </div>

    </div>
  );
};

export default DesktopBooking;
